/**
 * UpgradeRegistry - Registry for car upgrade definitions
 *
 * Manages upgrade templates that define costs, effects, and progression
 * for car upgrades (speed, acceleration, momentum, etc.).
 *
 * @example
 * import { upgradeRegistry } from './registries/UpgradeRegistry.js';
 *
 * // Load upgrades from JSON
 * upgradeRegistry.loadFromJSON(upgradesData);
 *
 * // Get a specific upgrade
 * const speedUpgrade = upgradeRegistry.get('baseSpeed');
 *
 * // Get all speed upgrades
 * const speedUpgrades = upgradeRegistry.getByCategory('speed');
 *
 * // Calculate cost for a specific level
 * const cost = upgradeRegistry.getUpgradeCost('baseSpeed', 3);
 */
import { Registry } from '../core/Registry.js';

// Valid upgrade categories
const VALID_CATEGORIES = ['speed', 'utility', 'momentum', 'special'];

/**
 * Extended Registry for upgrade definitions with upgrade-specific features
 */
export class UpgradeRegistry extends Registry {
    constructor() {
        super('Upgrades');
        this._categories = [];
        this._setupValidators();
    }

    /**
     * Set up upgrade-specific validators
     * @private
     */
    _setupValidators() {
        // Required fields validator
        this.addValidator((id, def) => {
            if (!def.name) return 'Upgrade must have a name';
            if (!def.category) return 'Upgrade must have a category';
            if (def.baseCost === undefined) return 'Upgrade must have a baseCost';
            if (!def.effect) return 'Upgrade must have an effect';
            return null;
        });

        // Valid category validator
        this.addValidator((id, def) => {
            if (!VALID_CATEGORIES.includes(def.category)) {
                return `Invalid category '${def.category}'. Must be one of: ${VALID_CATEGORIES.join(', ')}`;
            }
            return null;
        });

        // Cost scaling validator (must be > 1 if provided)
        this.addValidator((id, def) => {
            if (def.costScaling !== undefined && def.costScaling <= 1) {
                return `costScaling must be > 1, got ${def.costScaling}`;
            }
            return null;
        });

        // baseCost must be positive
        this.addValidator((id, def) => {
            if (def.baseCost <= 0) {
                return `baseCost must be positive, got ${def.baseCost}`;
            }
            return null;
        });
    }

    /**
     * Load upgrades from JSON data
     * @param {Object} data - JSON data with upgrades array and optional categories
     * @returns {this} The registry instance for chaining
     */
    loadFromJSON(data) {
        if (!data.upgrades || !Array.isArray(data.upgrades)) {
            throw new Error('[Upgrades] Invalid JSON: missing upgrades array');
        }

        // Store categories if provided
        if (data.categories && Array.isArray(data.categories)) {
            this._categories = [...data.categories];
        }

        // Register each upgrade
        for (const upgrade of data.upgrades) {
            if (!upgrade.id) {
                console.warn('[Upgrades] Skipping upgrade without id:', upgrade);
                continue;
            }
            this.register(upgrade.id, upgrade);
        }

        console.log(`[Upgrades] Loaded ${this.size} upgrades from JSON`);
        return this;
    }

    /**
     * Get all upgrades for a specific category
     * @param {string} category - Category to filter by
     * @returns {Array<Object>} Array of upgrades matching the category
     */
    getByCategory(category) {
        return this.getAll().filter(upgrade => upgrade.category === category);
    }

    /**
     * Get the loaded categories
     * @returns {string[]} Array of category names
     */
    getCategories() {
        return [...this._categories];
    }

    /**
     * Get list of valid categories
     * @returns {string[]} Array of valid category names
     */
    getValidCategories() {
        return [...VALID_CATEGORIES];
    }

    /**
     * Calculate the cost for an upgrade at a specific level
     *
     * Uses baseCosts array if available (for exact costs),
     * otherwise falls back to baseCost * costScaling^level formula.
     *
     * @param {string} id - Upgrade ID
     * @param {number} level - Current level (0-indexed, cost for upgrading FROM this level)
     * @returns {number|null} Cost in miles, or null if at max level
     */
    getUpgradeCost(id, level) {
        const upgrade = this.get(id);

        // Check if at max level
        if (upgrade.maxLevel !== undefined && level >= upgrade.maxLevel) {
            return null;
        }

        // Use explicit baseCosts array if available
        if (upgrade.baseCosts && Array.isArray(upgrade.baseCosts)) {
            if (level < upgrade.baseCosts.length) {
                return upgrade.baseCosts[level];
            }
            // Past the array length - use formula
        }

        // Fall back to scaling formula
        const costScaling = upgrade.costScaling || 1.15;
        return Math.floor(upgrade.baseCost * Math.pow(costScaling, level));
    }

    /**
     * Calculate the effect value for an upgrade at a specific level
     *
     * Effect calculation depends on the upgrade's effect.type and effect configuration.
     *
     * @param {string} id - Upgrade ID
     * @param {number} level - Current level (1-indexed)
     * @returns {Object} Effect information including value
     */
    getUpgradeEffect(id, level) {
        const upgrade = this.get(id);
        const effect = upgrade.effect;

        // Base effect object
        const result = {
            type: effect.type,
            description: effect.description,
            level
        };

        // Effect value calculation based on effect configuration
        if (effect.baseValue !== undefined && effect.scaling !== undefined) {
            // Scaling formula: baseValue * scaling^(level-1)
            result.value = effect.baseValue * Math.pow(effect.scaling, Math.max(0, level - 1));
        } else if (effect.baseValue !== undefined) {
            // Linear scaling: baseValue * level
            result.value = effect.baseValue * level;
        } else {
            // No value calculation - effect is defined elsewhere (e.g., in car config)
            result.value = null;
        }

        return result;
    }

    /**
     * Get max level for an upgrade
     * @param {string} id - Upgrade ID
     * @returns {number} Max level, or Infinity if not specified
     */
    getMaxLevel(id) {
        const upgrade = this.get(id);
        return upgrade.maxLevel !== undefined ? upgrade.maxLevel : Infinity;
    }
}

// Singleton instance for app-wide use
export const upgradeRegistry = new UpgradeRegistry();
